import React, { Suspense, useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Sky, Grid, PerspectiveCamera, Cloud, Text } from '@react-three/drei';
import { FacilityZone } from './3d/VenueDecorations';
import { StructureRenderer, FacilityMarker } from './3d/StructureRenderer';
import { RouteNavigationPath } from './3d/RouteNavigation';
import { PropRenderer } from './3d/PropRenderer';
import { LandmarkRenderer } from './3d/LandmarkRenderer';
import { VisitorMarker3D } from './3d/VisitorMarker3D';

const AGENCY_LOTS = [
  'Jabatan Kesihatan Negeri Melaka',
  'Jabatan Perhutanan',
  'Jabatan Kesihatan Negeri Melaka',
  'Lembaga Nanas Negara',
  'Bomba - JPBM Melaka',
  'Kash Honey & Bridge Well',
  'Prima',
  'Unifi',
  'PDMT',
  'Bank Rakyat Ayer Keroh',
  'Rekayu & Symmetry Energy',
  'Jabatan Pengairan dan Saliran',
  'SWCorp',
  'UCAM - Kolej Risda',
  'ILAM',
  'Kolej Komuniti Selandar',
  'Penaja',
  'SPNB & Pemaju GJH',
  'Digital Hustlaz',
  'Tenaga Nasional Berhad',
  'UPEN Melaka',
  'JPJ & RFS Teknologi',
  'IKPKT',
  'Syarikat Air Melaka',
  'MBMB Perancang & Kejuruteraan',
  'Saharen Structure',
];

const NURSERY_LOTS = [
  'Laris Melaka',
  'Pro Landscape Plant',
  'Mamee',
  'Bahtera Emas',
  'SJ Food Global',
  'Samika Resources',
  'Citarasa Alam',
  'Zhanarin Flowers',
  'Astro',
  'Decathlon',
  'One Garden Nursery',
  'Wild Impact Plant',
  'Pepokok Jasmin',
  'Mak Aini Secret Garden',
  'JPG Planterra',
  'An Nur Marketing',
  'Ninz Solution',
  'Zue Landscape',
  'Sinar Atif',
  'Yully Machinery',
  'The Daun Sky Garden',
  'Aero Farm City',
  'Qayser Khayra Nursery',
  'JRG Legacy',
  'ST Plant Paradise',
  'Sanyi Orchids',
  'SLS Succulent',
  'Roku Hachi Trading',
  'Rafa Argoplant',
  'Yong Fidah Enterprise',
];

const PBT_LOTS = [
  'MPAG',
  'MB Iskandar Puteri',
  'MBPJ',
  'MP Kemaman',
  'MPHTJ',
  'MB Seberang Perai',
  'MP Jasin',
  'MB Kuala Terengganu',
  'MD Bera',
  'MB Pasir Gudang',
];

function RainEffect({ intensity = 500 }) {
  const rainRef = useRef();

  const positions = useMemo(() => {
    const pos = new Float32Array(intensity * 3);
    for (let i = 0; i < intensity; i += 1) {
      pos[i * 3] = (Math.random() - 0.5) * 420;
      pos[i * 3 + 1] = Math.random() * 110;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 300;
    }
    return pos;
  }, [intensity]);

  useFrame((state, delta) => {
    if (!rainRef.current) return;
    const positionArray = rainRef.current.geometry.attributes.position.array;
    for (let i = 0; i < intensity; i += 1) {
      positionArray[i * 3 + 1] -= 60 * delta;
      if (positionArray[i * 3 + 1] < 0) positionArray[i * 3 + 1] = 110;
    }
    rainRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={rainRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={intensity} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial color="#a8c8ff" size={0.3} transparent opacity={0.7} sizeAttenuation />
    </points>
  );
}

function GroundLabel({ position, text, color = '#ffffff', size = 4, rotation = [-Math.PI / 2, 0, 0] }) {
  return (
    <Text position={position} rotation={rotation} fontSize={size} color={color} anchorX="center" anchorY="middle" maxWidth={42}>
      {text}
    </Text>
  );
}

function Zone({ position, size, color, label, labelColor = '#ffffff', height = 0.35, rotationY = 0 }) {
  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      <mesh position={[0, height / 2, 0]} receiveShadow castShadow>
        <boxGeometry args={[size[0], height, size[1]]} />
        <meshStandardMaterial color={color} roughness={0.82} metalness={0.03} />
      </mesh>
      {label && <GroundLabel position={[0, height + 0.06, 0]} text={label} color={labelColor} size={3.5} />}
    </group>
  );
}

function Booth({ number, name, position, color = '#ffffff', textColor = '#f97316', size = [8, 6], onSelect }) {
  return (
    <group position={position} onClick={(event) => {
      event.stopPropagation();
      onSelect?.({ id: number, name });
    }}>
      <mesh position={[0, 1.1, 0]} castShadow receiveShadow>
        <boxGeometry args={[size[0], 2.2, size[1]]} />
        <meshStandardMaterial color={color} roughness={0.72} />
      </mesh>
      <GroundLabel position={[0, 2.35, 0]} text={String(number)} color={textColor} size={3.5} />
      {name && <GroundLabel position={[0, 2.45, size[1] / 2 + 2.4]} text={name} color="#172554" size={1.4} />}
    </group>
  );
}

function BoothGrid({ start, rows, cols, gap = [10, 8], names = [], color, textColor, onSelect }) {
  const booths = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const number = row * cols + col + 1;
      booths.push(
        <Booth
          key={number}
          number={number}
          name={names[number - 1]}
          position={[start[0] + col * gap[0], 0, start[1] + row * gap[1]]}
          color={color}
          textColor={textColor}
          onSelect={onSelect}
        />,
      );
    }
  }
  return booths;
}

function FoodTruckRow() {
  return Array.from({ length: 10 }, (_, index) => (
    <Zone
      key={index}
      position={[-22, 0, 55 - index * 7]}
      size={[8, 3.2]}
      color="#3b82f6"
      label={index === 0 ? 'FOOD TRUCK' : ''}
      height={0.55}
    />
  ));
}

function OvalPbtCluster({ onSelect }) {
  const radiusX = 26;
  const radiusZ = 20;
  return (
    <group position={[10, 0, -8]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.05, 0]}>
        <ringGeometry args={[14, 25, 72]} />
        <meshStandardMaterial color="#111827" transparent opacity={0.18} />
      </mesh>
      <GroundLabel position={[0, 0.12, 0]} text="DBKL / MBMB\nANJUNG PBT" color="#ffffff" size={4.2} />
      {PBT_LOTS.map((name, index) => {
        const angle = (Math.PI * 1.15) - (index / (PBT_LOTS.length - 1)) * Math.PI * 1.9;
        return (
          <Booth
            key={name}
            number={index + 4}
            name={name}
            position={[Math.cos(angle) * radiusX, 0, Math.sin(angle) * radiusZ]}
            color="#2f6fc6"
            textColor="#ffffff"
            size={[9, 6]}
            onSelect={onSelect}
          />
        );
      })}
    </group>
  );
}

function VvipRoute() {
  return (
    <group>
      <Zone position={[45, 0, -88]} size={[250, 12]} color="#bdbdbd" label="LALUAN VVIP MASUK DAN KELUAR" labelColor="#1e3a8a" height={0.08} />
      <Zone position={[-70, 0.08, -88]} size={[38, 8]} color="#d1d5db" label="PARKING VIP" labelColor="#111827" height={0.18} />
      <Zone position={[25, 0.08, -88]} size={[46, 8]} color="#d1d5db" label="PARKING VIP" labelColor="#111827" height={0.18} />
      <Zone position={[112, 0.08, -88]} size={[46, 8]} color="#d1d5db" label="PARKING VIP" labelColor="#111827" height={0.18} />
    </group>
  );
}

function PdfBasedVenue({ onBoothSelect }) {
  return (
    <>
      <Zone position={[-86, 0, 34]} size={[25, 62]} color="#c95c0b" label="TAPAK MEMASAK\nKENDURI RAKYAT" />
      <Zone position={[-60, 0, 16]} size={[22, 32]} color="#f8fafc" label="8 KEMAH\nMAKAN" labelColor="#334155" />
      <FoodTruckRow />

      <Zone position={[5, 0, 35]} size={[28, 58]} color="#7433a8" label="TAPAK JUALAN\nRAHMAH\n15 PAMERAN" />
      <BoothGrid start={[32, 48]} rows={3} cols={10} names={NURSERY_LOTS} color="#ffffff" textColor="#16a34a" onSelect={onBoothSelect} />
      <Zone position={[64, 0, 20]} size={[62, 24]} color="#f8fafc" label="3 KEMAH MAKAN\n40 x 40" labelColor="#111827" rotationY={-0.04} />
      <Zone position={[95, 0, -20]} size={[70, 22]} color="#f8fafc" label="9 BAY MARQUEEN\nKEMAH UTAMA" labelColor="#111827" rotationY={-0.05} />

      <OvalPbtCluster onSelect={onBoothSelect} />

      <BoothGrid start={[-52, -58]} rows={2} cols={13} names={AGENCY_LOTS} color="#ffffff" textColor="#f97316" onSelect={onBoothSelect} />
      <Zone position={[-86, 0, -58]} size={[18, 10]} color="#f8fafc" label="KAJI SELIDIK\nAGIHAN POKOK" labelColor="#334155" />
      <Zone position={[-43, 0, -34]} size={[38, 12]} color="#2f6fc6" label="BAS KKM" />
      <Zone position={[-18, 0, -28]} size={[28, 10]} color="#2f6fc6" label="JENTERA\nBOMBA" />
      <Zone position={[5, 0, -31]} size={[16, 8]} color="#2f6fc6" label="JPJ" />

      <Zone position={[-96, 0, -18]} size={[6, 24]} color="#fef3c7" label="PINTU\nGERBANG" labelColor="#ffffff" rotationY={-0.15} />
      <Zone position={[138, 0, 3]} size={[28, 88]} color="#d1d5db" label="PARKING VVIP" labelColor="#111827" rotationY={-0.12} />
      <Zone position={[130, 0, -58]} size={[16, 12]} color="#2563eb" label="AMBULAN" />
      <Zone position={[138, 0, -82]} size={[12, 12]} color="#2563eb" label="BALAI\nPOLIS" />
      <Zone position={[120, 0, 12]} size={[10, 8]} color="#f8fafc" label="HOLDING\nROOM" labelColor="#334155" />
      <Zone position={[120, 0, 0]} size={[10, 4]} color="#3b82f6" label="TANDAS\nVIP" />
      <Zone position={[112, 0, -42]} size={[12, 12]} color="#f8fafc" label="MEDIC" labelColor="#334155" />

      <VvipRoute />
    </>
  );
}

function Ground({ weatherCondition }) {
  const isRaining = ['rainy', 'stormy'].includes(weatherCondition);
  const groundColor = isRaining ? '#3d4650' : '#5f6468';
  const grassColor = isRaining ? '#335c2f' : '#426f35';

  return (
    <>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.1, 0]} receiveShadow>
        <planeGeometry args={[440, 300]} />
        <meshStandardMaterial color={grassColor} roughness={0.95} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[12, 0, 0]} receiveShadow>
        <planeGeometry args={[300, 190]} />
        <meshStandardMaterial color={groundColor} roughness={0.9} />
      </mesh>
      <Grid
        args={[300, 190]}
        cellSize={5}
        cellThickness={0.18}
        cellColor="#737373"
        sectionSize={20}
        sectionThickness={0.45}
        sectionColor="#303030"
        fadeDistance={240}
        fadeStrength={2}
        followCamera={false}
        infiniteGrid={false}
        position={[12, 0.02, 0]}
      />
      <GroundLabel position={[0, 0.05, 82]} text="HLN 2026 - STADIUM HANG JEBAT EVENT MAP" color="#ffffff" size={5.5} />
    </>
  );
}

export default function ExhibitionScene3D({
  booths = [],
  facilities = [],
  props: sceneProps = [],
  landmarks = [],
  onBoothSelect,
  selectedBooth,
  weather,
  routeStart,
  routeEnd,
  routeActive,
  visitorWorldPos,
  visitorAccuracy,
}) {
  const isRaining = weather && ['rainy', 'stormy'].includes(weather.condition);
  const isCloudy = weather && weather.condition === 'cloudy';
  const isNight = weather && !weather.is_day;
  const hasAdminLayout = booths.length || facilities.length || sceneProps.length || landmarks.length;

  const skyProps = useMemo(() => {
    if (isRaining) return { turbidity: 20, rayleigh: 4, inclination: 0.3 };
    if (isCloudy) return { turbidity: 15, rayleigh: 3, inclination: 0.4 };
    if (isNight) return { turbidity: 3, rayleigh: 0.5, inclination: 0.85 };
    return { turbidity: 8, rayleigh: 2, inclination: 0.5 };
  }, [isRaining, isCloudy, isNight]);

  return (
    <Canvas
      shadows
      style={{ width: '100%', height: '100%' }}
      gl={{ antialias: true, powerPreference: 'high-performance' }}
      dpr={[1, 1.5]}
    >
      <PerspectiveCamera makeDefault position={[0, 145, 190]} fov={48} />
      <ambientLight intensity={isRaining ? 0.3 : isCloudy ? 0.4 : 0.55} />
      <directionalLight
        position={[90, 120, 60]}
        intensity={isRaining ? 0.5 : isCloudy ? 0.8 : 1.15}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-far={350}
        shadow-camera-left={-180}
        shadow-camera-right={180}
        shadow-camera-top={160}
        shadow-camera-bottom={-160}
      />
      <hemisphereLight intensity={0.5} groundColor="#426f35" />
      <Sky distance={450000} sunPosition={[100, 40, 100]} azimuth={0.25} {...skyProps} />

      {isRaining && <RainEffect intensity={weather.condition === 'stormy' ? 1000 : 500} />}
      {(isRaining || isCloudy) && (
        <>
          <Cloud position={[-40, 55, -40]} speed={0.3} opacity={isRaining ? 0.8 : 0.5} scale={30} />
          <Cloud position={[60, 60, 40]} speed={0.2} opacity={isRaining ? 0.9 : 0.6} scale={25} />
          <Cloud position={[100, 50, -80]} speed={0.15} opacity={isRaining ? 0.7 : 0.4} scale={35} />
        </>
      )}
      <fog attach="fog" args={[isRaining ? '#5a7080' : isCloudy ? '#8b9dab' : '#a8ccdd', 260, 540]} />

      <Suspense fallback={null}>
        <Ground weatherCondition={weather?.condition} />
        {!hasAdminLayout && <PdfBasedVenue onBoothSelect={onBoothSelect} selectedBooth={selectedBooth} />}
      </Suspense>

      {hasAdminLayout && (
        <>
          <Suspense fallback={null}>
            {booths.map((booth) => (
              <StructureRenderer
                key={booth.id}
                booth={booth}
                onSelect={onBoothSelect}
                isSelected={selectedBooth?.id === booth.id}
                weatherCondition={weather?.condition}
              />
            ))}
          </Suspense>

          <Suspense fallback={null}>
            {facilities.map((facility) => (
              <FacilityMarker key={`fac-${facility.id}`} facility={facility} />
            ))}
            {facilities
              .filter((facility) => ['prayer_room', 'first_aid', 'toilet', 'parking', 'entrance', 'info', 'food'].includes(facility.type))
              .map((facility) => (
                <FacilityZone
                  key={`zone-${facility.id}`}
                  position={[facility.position_x, 0, facility.position_z]}
                  type={facility.type}
                  label={facility.name}
                  radius={facility.type === 'parking' ? 9 : facility.type === 'entrance' ? 7 : 5}
                />
              ))}
          </Suspense>

          <Suspense fallback={null}>
            {sceneProps.map((prop) => (
              <PropRenderer key={`prop-${prop.id}`} prop={prop} />
            ))}
          </Suspense>

          <Suspense fallback={null}>
            {landmarks.map((landmark) => (
              <LandmarkRenderer key={`lm-${landmark.id}`} landmark={landmark} />
            ))}
          </Suspense>
        </>
      )}

      <RouteNavigationPath start={routeStart} end={routeEnd} active={routeActive} />
      <VisitorMarker3D worldPos={visitorWorldPos} accuracyMeters={visitorAccuracy} />

      <OrbitControls
        enablePan
        enableZoom
        enableRotate
        minDistance={35}
        maxDistance={420}
        maxPolarAngle={Math.PI / 2.05}
        target={[15, 0, -5]}
        enableDamping
        dampingFactor={0.05}
      />
    </Canvas>
  );
}
